import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getUser, isAdmin, isSecurityManager } from '@/utils/auth';
import api from '@/utils/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Clock,
  FileText,
  Image as ImageIcon,
  TrendingUp,
  BarChart3,
  Calendar,
  Sparkles,
  ArrowRight,
  Building2,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { toast } from 'sonner';

// ─── Helpers ────────────────────────────────────────────────────────────────

function getInitials(name) {
  if (!name) return 'U';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const pad = (n) => String(n).padStart(2, '0');
const fmtDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const PRESET_PERIODS = [
  { key: 'allTime', label: 'Semua Waktu' },
  { key: 'thisMonth', label: 'Bulan Ini' },
  { key: 'thisYear', label: 'Tahun Ini' },
  { key: 'custom', label: 'Rentang Tanggal' },
];

function computePresetRange(key) {
  const now = new Date();
  if (key === 'thisMonth') {
    return {
      fromDate: fmtDate(new Date(now.getFullYear(), now.getMonth(), 1)),
      toDate: fmtDate(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
    };
  }
  if (key === 'thisYear') {
    return {
      fromDate: fmtDate(new Date(now.getFullYear(), 0, 1)),
      toDate: fmtDate(new Date(now.getFullYear(), 11, 31)),
    };
  }
  return { fromDate: null, toDate: null };
}

function periodLabel(key, customFrom, customTo) {
  const now = new Date();
  if (key === 'thisMonth')
    return now.toLocaleDateString('id-ID', { month: 'long', year: 'numeric' });
  if (key === 'thisYear') return `Tahun ${now.getFullYear()}`;
  if (key === 'custom' && customFrom && customTo)
    return `${customFrom} s/d ${customTo}`;
  if (key === 'custom' && customFrom) return `Sejak ${customFrom}`;
  return 'Seluruh Periode';
}

// ─── Custom Chart Tooltip ────────────────────────────────────────────────────

function CustomChartTooltip({ active, payload, label }) {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 rounded-xl shadow-lg border border-slate-100 text-xs space-y-1">
        <p className="font-bold text-slate-900">{payload[0].payload.fullName || label}</p>
        <p className="text-primary font-medium flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5" />
          <span>
            Total: <strong className="text-slate-900">{Number(payload[0].value).toFixed(1)}</strong> jam
          </span>
        </p>
      </div>
    );
  }
  return null;
}

// ─── Stat Card ───────────────────────────────────────────────────────────────

function StatCard({ icon, title, value, unit, colorBg, colorText, subtext }) {
  return (
    <Card
      className="p-5 bg-white border-slate-200/80 shadow-sm hover:shadow-md transition-all duration-200"
      data-testid={`stat-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</p>
          <div className="flex items-baseline gap-1.5 mt-2">
            <span className="text-3xl font-bold font-heading text-slate-900">{value}</span>
            {unit && <span className="text-xs font-medium text-muted-foreground">{unit}</span>}
          </div>
        </div>
        <div className={`p-3 rounded-2xl ${colorBg} ${colorText}`}>
          {React.createElement(icon, { className: 'w-6 h-6' })}
        </div>
      </div>
      {subtext && (
        <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
          <span>{subtext}</span>
        </p>
      )}
    </Card>
  );
}

// ─── Filter Bar ──────────────────────────────────────────────────────────────

function FilterBar({ period, onPeriodChange, customFrom, customTo, onCustomChange }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
      {/* Pill Tabs */}
      <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-xl">
        {PRESET_PERIODS.map((p) => (
          <button
            key={p.key}
            onClick={() => onPeriodChange(p.key)}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all duration-150 whitespace-nowrap ${
              period === p.key
                ? 'bg-white text-[#1A3C34] shadow-sm'
                : 'text-slate-500 hover:text-slate-700 hover:bg-white/60'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Custom date inputs — hanya tampil jika "Rentang Tanggal" dipilih */}
      {period === 'custom' && (
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 shadow-sm">
            <Calendar className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <input
              type="date"
              value={customFrom}
              onChange={(e) => onCustomChange('from', e.target.value)}
              className="text-xs text-slate-700 outline-none bg-transparent w-32"
            />
          </div>
          <span className="text-xs text-slate-400 font-medium">s/d</span>
          <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 shadow-sm">
            <Calendar className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <input
              type="date"
              value={customTo}
              onChange={(e) => onCustomChange('to', e.target.value)}
              className="text-xs text-slate-700 outline-none bg-transparent w-32"
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function Dashboard() {
  const user = getUser();

  const [period, setPeriod] = useState('thisMonth');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const [stats, setStats] = useState({
    totalCheckIns: 0,
    totalTimesheets: 0,
    totalScreenshots: 0,
    totalHours: 0,
    totalTeams: 0,
  });
  const [chartData, setChartData] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  // Resolve active date range based on selected period
  const getActiveDateRange = useCallback(() => {
    if (period === 'custom') {
      return { fromDate: customFrom || null, toDate: customTo || null };
    }
    return computePresetRange(period);
  }, [period, customFrom, customTo]);

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const { fromDate, toDate } = getActiveDateRange();
      if (fromDate && toDate && fromDate > toDate) {
        toast.error('Tanggal mulai tidak boleh melebihi tanggal selesai');
        setLoading(false);
        return;
      }
      const role = user?.role;

      const dateParams = {};
      if (fromDate) dateParams.from_date = fromDate;
      if (toDate) dateParams.to_date = toDate;

      const workHoursParams = { ...dateParams };
      if (isSecurityManager()) workHoursParams.group_by = 'team';
      else if (role === 'employee') workHoursParams.group_by = 'day';

      const results = await Promise.allSettled([
        api.get('/checkins', { params: dateParams }),
        api.get('/timesheets', { params: dateParams }),
        api.get('/screenshots', { params: dateParams }),
        api.get('/reports/work-hours', { params: workHoursParams }),
        isSecurityManager() ? api.get('/teams') : Promise.resolve({ data: [] }),
      ]);

      const [checkinsRes, timesheetsRes, screenshotsRes, workHoursRes, teamsRes] = results.map((r) =>
        r.status === 'fulfilled' ? r.value : { data: [] }
      );
      results.forEach((r, idx) => {
        if (r.status === 'rejected') {
          const names = ['checkins', 'timesheets', 'screenshots', 'work-hours', 'teams'];
          console.error(`Dashboard fetch failed for ${names[idx]}:`, r.reason);
        }
      });

      const checkins = checkinsRes.data || [];
      const timesheets = timesheetsRes.data || [];
      const screenshots = screenshotsRes.data || [];
      const workHours = workHoursRes.data || [];
      const teams = teamsRes.data || [];

      // Total hours dari work-hours endpoint (sudah difilter backend)
      const totalHoursSum = workHours.reduce((acc, r) => acc + (r.total_hours || 0), 0);

      // Build chart data dari work-hours response
      let chart = [];
      if (isSecurityManager()) {
        chart = workHours
          .filter((r) => r.total_hours > 0)
          .map((r) => ({
            name: r.team_name,
            fullName: r.team_name,
            hours: r.total_hours,
          }))
          .sort((a, b) => b.hours - a.hours);
      } else if (role === 'employee') {
        chart = workHours
          .filter((r) => r.total_hours > 0)
          .map((r) => ({
            name: r.date,
            fullName: r.date,
            hours: r.total_hours,
          }))
          .sort((a, b) => b.hours - a.hours);
      } else {
        chart = workHours
          .filter((r) => r.total_hours > 0)
          .map((r) => ({
            name: r.user_name ? r.user_name.split(' ')[0] : 'User',
            fullName: r.user_name || 'User',
            hours: r.total_hours,
          }))
          .sort((a, b) => b.hours - a.hours);
      }

      setChartData(chart);

      setStats({
        totalCheckIns: checkins.length,
        totalTimesheets: timesheets.length,
        totalScreenshots: screenshots.length,
        totalHours: Number(totalHoursSum.toFixed(1)),
        totalTeams: teams.length,
      });

      // Aktivitas terbaru
      const activities = [
        ...checkins.slice(0, 5).map((c) => ({
          type: 'check-in',
          message: c.check_out_time
            ? `Selesai kerja (${c.hours_worked || 0} jam)`
            : 'Check-in aktif',
          time: c.check_in_time,
          user: c.user_name,
        })),
        ...timesheets.slice(0, 5).map((t) => ({
          type: 'timesheet',
          message: `Timesheet: ${t.task_name || `${t.hours_worked} jam kerja`}`,
          time: t.created_at || t.date,
          user: t.user_name,
        })),
      ]
        .sort((a, b) => new Date(b.time) - new Date(a.time))
        .slice(0, isSecurityManager() ? 0 : 4);

      setRecentActivity(activities);
    } catch (error) {
      toast.error('Gagal memuat data dashboard');
    } finally {
      setLoading(false);
    }
  }, [getActiveDateRange, user?.role]);

  // Re-fetch on period change; for custom, only re-fetch when both dates are set
  useEffect(() => {
    if (period === 'custom') {
      if (customFrom && customTo) fetchDashboardData();
    } else {
      fetchDashboardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, customFrom, customTo]);

  const handleCustomChange = (field, val) => {
    if (field === 'from') setCustomFrom(val);
    else setCustomTo(val);
  };

  // ── Loading state ──────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="pb-2 border-b border-slate-200/70">
          <div className="flex items-center gap-2">
            <h1 className="text-3xl lg:text-4xl font-bold font-heading text-slate-900 tracking-tight" data-testid="dashboard-title">
              Dashboard
            </h1>
          </div>
          {user?.role === 'admin' && (
            <p className="text-muted-foreground text-sm mt-1">
              Selamat datang kembali, <strong className="text-slate-800">{user?.name}</strong>! Ringkasan produktivitas kerja tim Anda.
            </p>
          )}
          {!isAdmin() && (
            <p className="text-muted-foreground text-sm mt-1">
              Selamat datang kembali, <strong className="text-slate-800">{user?.name}</strong>!
            </p>
          )}
        </div>
        <div className="flex flex-col items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-3"></div>
          <p className="text-sm text-muted-foreground">Memuat data dashboard...</p>
        </div>
      </div>
    );
  }

  const activeLabel = periodLabel(period, customFrom, customTo);

  return (
    <div className="space-y-8">
      {/* ── Header ── */}
      <div className="pb-2 border-b border-slate-200/70">
        <div className="flex items-center gap-2">
          <h1 className="text-3xl lg:text-4xl font-bold font-heading text-slate-900 tracking-tight" data-testid="dashboard-title">
            Dashboard
          </h1>
        </div>
        {user?.role === 'admin' && (
          <p className="text-muted-foreground text-sm mt-1">
            Selamat datang kembali, <strong className="text-slate-800">{user?.name}</strong>! Ringkasan produktivitas kerja tim Anda.
          </p>
        )}
        {user?.role !== 'admin' && (
          <p className="text-muted-foreground text-sm mt-1">
            Selamat datang kembali, <strong className="text-slate-800">{user?.name}</strong>!
          </p>
        )}
      </div>

      {/* ── Filter Bar ── */}
      <div className="flex flex-col gap-3">
        <FilterBar
          period={period}
          onPeriodChange={setPeriod}
          customFrom={customFrom}
          customTo={customTo}
          onCustomChange={handleCustomChange}
        />
        <p className="text-xs text-slate-500 flex items-center gap-1.5">
          <Calendar className="w-3.5 h-3.5 text-slate-400" />
          Data periode: <span className="font-semibold text-[#1A3C34]">{activeLabel}</span>
        </p>
      </div>

      {/* ── 4 Metric Cards ── */}
      <div className={`grid grid-cols-1 sm:grid-cols-2 ${isSecurityManager() ? 'lg:grid-cols-5' : 'lg:grid-cols-4'} gap-5`}>
        {isSecurityManager() && (
          <StatCard
            icon={Building2}
            title="Total Teams"
            value={stats.totalTeams}
            unit="team"
            colorBg="bg-teal-50"
            colorText="text-teal-700"
            subtext="Jumlah total team terdaftar"
          />
        )}
        <StatCard
          icon={Clock}
          title="Total Check-ins"
          value={stats.totalCheckIns}
          unit="sesi"
          colorBg="bg-emerald-50"
          colorText="text-emerald-700"
          subtext="Catatan kehadiran karyawan"
        />
        <StatCard
          icon={TrendingUp}
          title="Total Jam Kerja"
          value={stats.totalHours}
          unit="jam"
          colorBg="bg-[#1A3C34]/10"
          colorText="text-[#1A3C34]"
          subtext="Akumulasi waktu kerja produktif"
        />
        <StatCard
          icon={FileText}
          title="Timesheets"
          value={stats.totalTimesheets}
          unit="entri"
          colorBg="bg-amber-50"
          colorText="text-amber-700"
          subtext="Pencatatan tugas harian"
        />
        <StatCard
          icon={ImageIcon}
          title="Screenshots"
          value={stats.totalScreenshots}
          unit="foto"
          colorBg="bg-indigo-50"
          colorText="text-indigo-700"
          subtext="Bukti tangkapan layar aktivitas"
        />
      </div>

      {/* ── Main Grid ── */}
      <div className={`grid grid-cols-1 ${isSecurityManager() ? 'lg:grid-cols-1' : 'lg:grid-cols-3'} gap-6`}>
        {/* Left: Bar Chart */}
        <Card className={`${isSecurityManager() ? 'col-span-1' : 'lg:col-span-2'} p-6 bg-white border-slate-200/80 shadow-sm`}>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-5 border-b border-slate-100 gap-2">
            <div>
              <h2 className="text-xl font-bold font-heading text-slate-900 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary" />
                {isSecurityManager()
                  ? 'Grafik Jam Kerja per Team'
                  : user?.role === 'employee'
                  ? 'Grafik Jam Kerja Harian'
                  : 'Grafik Jam Kerja Karyawan'}
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                 {isSecurityManager()
                  ? `Akumulasi jam kerja tiap team — ${activeLabel}`
                  : user?.role === 'employee'
                  ? `Jam kerja harian — ${activeLabel}`
                  : `Akumulasi jam kerja tiap anggota tim — ${activeLabel}`}
              </p>
            </div>
            <Link to="/reports">
              <Button variant="ghost" size="sm" className="text-xs text-primary hover:text-primary/80 hover:bg-primary/5 h-8">
                <span>Lihat Laporan Detail</span>
                <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </Link>
          </div>

          {chartData.length === 0 ? (
            <div className="py-20 text-center text-muted-foreground">
              <BarChart3 className="w-10 h-10 mx-auto text-slate-300 mb-2" />
              <p className="text-sm font-medium">Belum ada data jam kerja untuk divisualisasikan</p>
              <p className="text-xs mt-1">Coba pilih periode waktu yang lain</p>
            </div>
          ) : (
            <div className="mt-6 w-full h-[320px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                  <XAxis
                    dataKey="name"
                    stroke="#64748B"
                    fontSize={12}
                    tickLine={false}
                    axisLine={{ stroke: '#E2E8F0' }}
                    dy={10}
                  />
                  <YAxis
                    stroke="#64748B"
                    fontSize={12}
                    tickLine={false}
                    axisLine={{ stroke: '#E2E8F0' }}
                  />
                  <Tooltip content={<CustomChartTooltip />} />
                  <Bar dataKey="hours" fill="#1A3C34" radius={[6, 6, 0, 0]} maxBarSize={44} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-accent" />
              <span>Diperbarui otomatis saat karyawan check-out</span>
            </span>
            <span className="font-semibold text-slate-700">Satuan: Jam Kerja</span>
          </div>
        </Card>

        {!isSecurityManager() && (
          <Card className="p-6 bg-white border-slate-200/80 shadow-sm flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between pb-5 border-b border-slate-100">
                <div>
                  <h2 className="text-lg font-bold font-heading text-slate-900" data-testid="recent-activity-title">
                    Aktivitas Terbaru
                  </h2>
                  <p className="text-xs text-muted-foreground mt-0.5">Log presensi &amp; timesheet terkini</p>
                </div>
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              </div>

              <div className="mt-5 space-y-3.5">
                {recentActivity.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Clock className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                    <p className="text-xs">Belum ada aktivitas tercatat pada periode ini</p>
                  </div>
                ) : (
                  recentActivity.map((activity, index) => {
                    const isCheckIn = activity.type === 'check-in';
                    return (
                      <div
                        key={index}
                        data-testid={`activity-item-${index}`}
                        className="flex items-start gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors border border-transparent hover:border-slate-100"
                      >
                        <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary font-bold text-xs flex items-center justify-center shrink-0 mt-0.5">
                          {getInitials(activity.user)}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-1">
                            <p className="text-xs font-semibold text-slate-900 truncate">
                              {activity.user || user?.name}
                            </p>
                            <Badge
                              variant="outline"
                              className={`text-[10px] py-0 px-1.5 font-medium ${
                                isCheckIn
                                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                  : 'bg-amber-50 text-amber-700 border-amber-200'
                              }`}
                            >
                              {isCheckIn ? 'Presensi' : 'Timesheet'}
                            </Badge>
                          </div>
                          <p className="text-xs text-slate-600 mt-0.5 truncate">{activity.message}</p>
                          <p className="text-[10px] text-muted-foreground mt-1">
                            {new Date(activity.time).toLocaleString('id-ID', {
                              day: '2-digit',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            <div className="mt-6 pt-4 border-t border-slate-100">
              <Link to="/reports" className="w-full block">
                <Button variant="outline" className="w-full text-xs text-slate-700 hover:text-slate-900 bg-slate-50 hover:bg-slate-100 h-9 rounded-xl">
                  <span>Buka Rekap Lengkap Presensi</span>
                  <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                </Button>
              </Link>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}